import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import {
  FaArrowLeft, // Added for purchase details
  FaArrowRight,
  FaCalendarAlt,
  FaGraduationCap,
  FaHeadset,
  FaInfoCircle,
  FaListAlt,
  FaMoneyBillWave,
  FaPhone,
  FaQuestionCircle,
  FaReceipt,
  FaShoppingBag,
  FaShoppingCart,
  FaSignOutAlt,
  FaUser
} from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../api/action/user';
import { useEducationExamCopy } from '../../domains/education/copy/educationExamCopy.js';
import { getUserDisplayName } from '../../utils/format.js';
import { getPurchaseSectionLabel } from '../../utils/subscriptionDisplay';


const CustomBackButton = ({ onClick, className = '' }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate('/');
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`group flex items-center space-x-2 text-gray-700 hover:text-blue-600 font-medium transition-all duration-300 hover:pl-1 pr-3 py-2 rounded-lg hover:bg-gradient-to-r hover:from-blue-50 hover:to-transparent ${className}`}
    >
      <div className="w-8 h-8 flex items-center justify-center bg-gray-100 group-hover:bg-blue-100 rounded-lg transition-colors duration-300">
        <FaArrowLeft className="h-4 w-4 group-hover:scale-110 transition-transform duration-300" />
      </div>
      <span className="font-semibold">Back to Home</span>
    </button>
  );
};


const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const UserProfile = () => {
  const navigate = useNavigate();
  // Identity on the Default template; rewrites course/enrollment wording on the Exam template.
  const examCopy = useEducationExamCopy();
  const domain = localStorage.getItem('domainType');
  const dispatch = useDispatch();
  const userDetail = useSelector((state) => state?.auth?.data);
  const [userStatus, setUserStatus] = useState(null);
  const authState = useSelector((state) => state?.auth?.data) || {};
  const userName = getUserDisplayName(authState);
  // Companies selling subscription courses show subscriptions alongside
  // one-off purchases, so the card is labelled for both.
  const company = useSelector((state) => state?.companyReducer?.company);
  const purchaseSectionLabel = getPurchaseSectionLabel(company);

  const handleLogout = async () => {
    try {
      await logout()(dispatch);
      navigate("/sign-in");
    } catch {
      navigate("/sign-in");
    }
  };

  const handleHomeClick = () => {
    navigate(-1);
  };

  useEffect(() => {
    const user = userDetail;
    if (user && user && user.status) {
      setUserStatus(user.status);
    }
  }, [userDetail]);

  const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated);
  const isLoading = useSelector((state) => state?.auth?.loading);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/sign-in');
    }
  }, [isAuthenticated, isLoading, navigate]);

  const themeColor = domain === 'EDUCATION' ? 'var(--education-primary, #746eac)' :
    domain === 'FLEX_OFFICE' ? '#3a7ca5' :
      domain === 'RETAIL' ? '#d35400' :
        '#b5a262';

  if (domain === 'INSTITUTION' && userStatus === 'EMAIL_VERIFIED') {
    return (
      <div className="text-center py-10">
        <h2 className="text-2xl font-semibold text-red-500">
          Application Pending
        </h2>
        <p className="text-gray-700 mt-2">
          Your application process is still pending.<br />
          Please complete the required steps to access the user dashboard.
        </p>
        <div className="mt-4 flex justify-center gap-4">
          <button
            onClick={() => navigate('/additional-info')}
            className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition duration-300"
          >
            Resume Application
          </button>
          <button
            onClick={handleLogout}
            className="px-6 py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition duration-300 flex items-center justify-center"
          >
            <FaSignOutAlt className="h-5 w-5 mr-2" />
            Logout
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen mt-10">

      <div className="mx-auto max-w-6xl pt-2">
        <div className="flex justify-between items-center mb-8">
          <div className="fixed top-4 left-4 z-20" style={{ paddingTop: '80px' }}>
            <CustomBackButton
              onClick={handleHomeClick}
              className="fixed top-24 left-4 z-20"
            />
          </div>
        </div>
        {domain !== 'FLEX_OFFICE' && (
          <div className="flex justify-end">
            <button
              onClick={handleLogout}
              className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-300 flex items-center"
            >
              <FaSignOutAlt className="h-5 w-5 mr-2" />
              Logout
            </button>
          </div>
        )}

        {domain === 'FLEX_OFFICE' && (<>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8 text-center pt-12"
          >
            <span className="mb-2 inline-block rounded-lg bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
              Welcome back
            </span>

            <h1 className="text-3xl font-bold text-gray-900 md:text-4xl">
              {userName}'s Dashboard
            </h1>
            <p className="mt-2 text-gray-600">
              Everything you need in one place
            </p>
          </motion.div>

        </>
        )}
        {domain !== 'FLEX_OFFICE' && (<>

          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center font-display text-4xl font-bold tracking-tight text-slate-900 md:text-5xl mb-8"
          >
            Dashboard
          </motion.h1>
        </>
        )}

        {domain === 'INSTITUTION' && (
          <motion.ul
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.2,
                },
              },
            }}
            className="mt-8 grid grid-cols-1 gap-8 text-center text-slate-700 md:grid-cols-2 lg:grid-cols-3"
          >
            <motion.li
              variants={cardVariants}
              className="rounded-xl bg-white px-8 py-10 shadow-lg hover:shadow-xl transition-shadow duration-300"
              style={{ backgroundColor: `${themeColor}20`, minHeight: '250px' }}
            >
              <button onClick={() => navigate('/my-profile')} className="w-full text-center group">
                <FaUser className="mx-auto h-12 w-12 text-blue-500 mb-4 group-hover:text-blue-600 transition-colors duration-300" />
                <h3 className="text-xl font-display font-semibold group-hover:text-blue-600 transition-colors duration-300">
                  Personal Info
                </h3>
                <p className="mt-2 text-gray-600">Manage your personal details and preferences.</p>
              </button>
            </motion.li>
            {userDetail.status !== 'NEW' && (
              <motion.li
                variants={cardVariants}
                className="rounded-xl bg-white px-8 py-10 shadow-lg hover:shadow-xl transition-shadow duration-300"
                style={{ backgroundColor: `${themeColor}20`, minHeight: '250px' }}
              >
                <button onClick={() => navigate('/fee-payment')} className="w-full text-center group">
                  <FaMoneyBillWave className="mx-auto h-12 w-12 text-green-500 mb-4 group-hover:text-green-600 transition-colors duration-300" />
                  <h3 className="text-xl font-display font-semibold group-hover:text-green-600 transition-colors duration-300">
                    Fee Payment
                  </h3>
                  <p className="mt-2 text-gray-600">Securely manage your tuition and other payments.</p>
                </button>
              </motion.li>
            )}
            <motion.li
              variants={cardVariants}
              className="rounded-xl bg-white px-8 py-10 shadow-lg hover:shadow-xl transition-shadow duration-300"
              style={{ backgroundColor: `${themeColor}20`, minHeight: '250px' }}
            >
              <button onClick={() => navigate('/my-application')} className="w-full text-center group">
                <FaListAlt className="mx-auto h-12 w-12 text-green-500 mb-4 group-hover:text-green-600 transition-colors duration-300" />
                <h3 className="text-xl font-display font-semibold group-hover:text-green-600 transition-colors duration-300">
                  My Applications
                </h3>
                <p className="mt-2 text-gray-600">View and manage your submitted applications.</p>
              </button>
            </motion.li>
          </motion.ul>
        )}

        {domain === 'EDUCATION' && (
          <motion.ul
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.2,
                },
              },
            }}
            className="mt-8 grid grid-cols-1 gap-8 text-center text-slate-700 md:grid-cols-2 lg:grid-cols-3"
          >
            <motion.li
              variants={cardVariants}
              className="rounded-xl bg-white px-8 py-10 shadow-lg hover:shadow-xl transition-shadow duration-300"
              style={{ backgroundColor: `${themeColor}20`, minHeight: '250px' }}
            >
              <button onClick={() => navigate('/my-profile')} className="w-full text-center group">
                <FaUser className="mx-auto h-12 w-12 text-blue-500 mb-4 group-hover:text-blue-600 transition-colors duration-300" />
                <h3 className="text-xl font-display font-semibold group-hover:text-blue-600 transition-colors duration-300">
                  Personal Info
                </h3>
                <p className="mt-2 text-gray-600">Manage your personal details and preferences.</p>
              </button>
            </motion.li>

            <motion.li
              variants={cardVariants}
              className="rounded-xl bg-white px-8 py-10 shadow-lg hover:shadow-xl transition-shadow duration-300"
              style={{ backgroundColor: `${themeColor}20`, minHeight: '250px' }}
            >
              <button onClick={() => navigate('/myCourses')} className="w-full text-center group">
                <FaGraduationCap className="mx-auto h-12 w-12 text-orange-500 mb-4 group-hover:text-orange-600 transition-colors duration-300" />
                <h3 className="text-xl font-display font-semibold group-hover:text-orange-600 transition-colors duration-300">
                  {examCopy('My Enrollments')}
                </h3>
                <p className="mt-2 text-gray-600">{examCopy('View and manage your enrolled courses.')}</p>
              </button>
            </motion.li>

            {/* ADDED: View Purchase Details / My Orders for EDUCATION */}
            <motion.li
              variants={cardVariants}
              className="rounded-xl bg-white px-8 py-10 shadow-lg hover:shadow-xl transition-shadow duration-300"
              style={{ backgroundColor: `${themeColor}20`, minHeight: '250px' }}
            >
              <button onClick={() => navigate('/my-orders')} className="w-full text-center group">
                <FaReceipt className="mx-auto h-12 w-12 text-purple-500 mb-4 group-hover:text-purple-600 transition-colors duration-300" />
                <h3 className="text-xl font-display font-semibold group-hover:text-purple-600 transition-colors duration-300">
                  {purchaseSectionLabel}
                </h3>
                <p className="mt-2 text-gray-600">{examCopy('View your order purchase receipts.')}</p>
              </button>
            </motion.li>

            <motion.li
              variants={cardVariants}
              className="rounded-xl bg-white px-8 py-10 shadow-lg hover:shadow-xl transition-shadow duration-300"
              style={{ backgroundColor: `${themeColor}20`, minHeight: '250px' }}
            >
              <button onClick={() => navigate('/contact')} className="w-full text-center group">
                <FaQuestionCircle className="mx-auto h-12 w-12 text-yellow-500 mb-4 group-hover:text-yellow-600 transition-colors duration-300" />
                <h3 className="text-xl font-display font-semibold group-hover:text-yellow-600 transition-colors duration-300">
                  Help
                </h3>
                <p className="mt-2 text-gray-600">Get assistance and support.</p>
              </button>
            </motion.li>
          </motion.ul>
        )}

        {domain === 'FLEX_OFFICE' && (
          <>
            <motion.ul
              initial="hidden"
              animate="visible"
              variants={{
                visible: {
                  transition: {
                    staggerChildren: 0.15,
                  },
                },
              }}
              className="mt-8 grid grid-cols-1 gap-6 text-center text-slate-700 md:grid-cols-2 lg:grid-cols-3"
            >
              {/* Card 1 - Personal Info */}
              <motion.li variants={cardVariants} className="group relative">
                <button
                  onClick={() => navigate('/my-profile')}
                  className="w-full text-left p-8 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-2xl hover:shadow-blue-100/50 transition-all duration-500 hover:-translate-y-2"
                >
                  <div className="flex flex-col items-start">
                    <div className="mb-6 w-16 h-16 rounded-2xl bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center transition-colors duration-300">
                      <FaUser className="h-8 w-8 text-blue-600 group-hover:text-blue-700 transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-700 transition-colors duration-300">
                      Personal Info
                    </h3>
                    <p className="text-gray-600 text-left leading-relaxed group-hover:text-gray-800 transition-colors duration-300">
                      Manage your personal details and workspace preferences
                    </p>
                    <div className="mt-6 flex items-center text-sm text-blue-600 font-medium group-hover:text-blue-700 transition-colors duration-300">
                      <span>View Profile</span>
                      <FaArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </div>
                </button>
              </motion.li>

              {/* Card 2 - My Bookings */}
              <motion.li variants={cardVariants} className="group relative">
                <button
                  onClick={() => navigate('/my-bookings')}
                  className="w-full text-left p-8 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl hover:border-green-300 hover:shadow-2xl hover:shadow-green-100/50 transition-all duration-500 hover:-translate-y-2"
                >
                  <div className="flex flex-col items-start">
                    <div className="mb-6 w-16 h-16 rounded-2xl bg-green-100 group-hover:bg-green-200 flex items-center justify-center transition-colors duration-300">
                      <FaCalendarAlt className="h-8 w-8 text-green-600 group-hover:text-green-700 transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-700 transition-colors duration-300">
                      My Bookings
                    </h3>
                    <p className="text-gray-600 text-left leading-relaxed group-hover:text-gray-800 transition-colors duration-300">
                      View and manage your workspace bookings & reservations
                    </p>
                    <div className="mt-6 flex items-center text-sm text-green-600 font-medium group-hover:text-green-700 transition-colors duration-300">
                      <span>Manage Bookings</span>
                      <FaArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </div>
                </button>
              </motion.li>

              {/* Card 3 - Support */}
              <motion.li variants={cardVariants} className="group relative">
                <button
                  onClick={() => navigate('/view-service/enquire-now')}
                  className="w-full text-left p-8 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl hover:border-yellow-300 hover:shadow-2xl hover:shadow-yellow-100/50 transition-all duration-500 hover:-translate-y-2"
                >
                  <div className="flex flex-col items-start">
                    <div className="mb-6 w-16 h-16 rounded-2xl bg-yellow-100 group-hover:bg-yellow-200 flex items-center justify-center transition-colors duration-300">
                      <FaHeadset className="h-8 w-8 text-yellow-600 group-hover:text-yellow-700 transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-yellow-700 transition-colors duration-300">
                      Support
                    </h3>
                    <p className="text-gray-600 text-left leading-relaxed group-hover:text-gray-800 transition-colors duration-300">
                      Get help with your bookings or workspace assistance
                    </p>
                    <div className="mt-6 flex items-center text-sm text-yellow-600 font-medium group-hover:text-yellow-700 transition-colors duration-300">
                      <span>Get Help</span>
                      <FaArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </div>
                </button>
              </motion.li>
            </motion.ul>
          </>
        )}

        {domain === 'RETAIL' && (
          <motion.ul
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.2,
                },
              },
            }}
            className="mt-8 grid grid-cols-1 gap-8 text-center text-slate-700 md:grid-cols-2 lg:grid-cols-3"
          >
            <motion.li
              variants={cardVariants}
              className="rounded-xl bg-white px-8 py-10 shadow-lg hover:shadow-xl transition-shadow duration-300"
              style={{ backgroundColor: `${themeColor}20`, minHeight: '250px' }}
            >
              <button onClick={() => navigate('/my-profile')} className="w-full text-center group">
                <FaUser className="mx-auto h-12 w-12 text-blue-500 mb-4 group-hover:text-blue-600 transition-colors duration-300" />
                <h3 className="text-xl font-display font-semibold group-hover:text-blue-600 transition-colors duration-300">
                  Personal Info
                </h3>
                <p className="mt-2 text-gray-600">Manage your personal details and preferences.</p>
              </button>
            </motion.li>

            <motion.li
              variants={cardVariants}
              className="rounded-xl bg-white px-8 py-10 shadow-lg hover:shadow-xl transition-shadow duration-300"
              style={{ backgroundColor: `${themeColor}20`, minHeight: '250px' }}
            >
              <button
                onClick={() => navigate('/my-orders')}
                className="w-full text-center group"
              >
                <FaShoppingBag className="mx-auto h-12 w-12 text-green-500 mb-4 group-hover:text-green-600 transition-colors duration-300" />
                <h3 className="text-xl font-display font-semibold group-hover:text-green-600 transition-colors duration-300">
                  My Orders
                </h3>
                <p className="mt-2 text-gray-600">View and track your order history.</p>
              </button>
            </motion.li>

            <motion.li
              variants={cardVariants}
              className="rounded-xl bg-white px-8 py-10 shadow-lg hover:shadow-xl transition-shadow duration-300"
              style={{ backgroundColor: `${themeColor}20`, minHeight: '250px' }}
            >
              <button onClick={() => navigate('/cart')} className="w-full text-center group">
                <FaShoppingCart className="mx-auto h-12 w-12 text-orange-500 mb-4 group-hover:text-orange-600 transition-colors duration-300" />
                <h3 className="text-xl font-display font-semibold group-hover:text-orange-600 transition-colors duration-300">
                  Shopping Cart
                </h3>
                <p className="mt-2 text-gray-600">View and manage items in your cart.</p>
              </button>
            </motion.li>

            <motion.li
              variants={cardVariants}
              className="rounded-xl bg-white px-8 py-10 shadow-lg hover:shadow-xl transition-shadow duration-300"
              style={{ backgroundColor: `${themeColor}20`, minHeight: '250px' }}
            >
              <button onClick={() => navigate('/contact')} className="w-full text-center group">
                <FaPhone className="mx-auto h-12 w-12 text-purple-500 mb-4 group-hover:text-purple-600 transition-colors duration-300" />
                <h3 className="text-xl font-display font-semibold group-hover:text-purple-600 transition-colors duration-300">
                  Contact
                </h3>
                <p className="mt-2 text-gray-600">Get in touch with our support team.</p>
              </button>
            </motion.li>

            <motion.li
              variants={cardVariants}
              className="rounded-xl bg-white px-8 py-10 shadow-lg hover:shadow-xl transition-shadow duration-300"
              style={{ backgroundColor: `${themeColor}20`, minHeight: '250px' }}
            >
              <button onClick={() => navigate('/help')} className="w-full text-center group">
                <FaInfoCircle className="mx-auto h-12 w-12 text-yellow-500 mb-4 group-hover:text-yellow-600 transition-colors duration-300" />
                <h3 className="text-xl font-display font-semibold group-hover:text-yellow-600 transition-colors duration-300">
                  Help
                </h3>
                <p className="mt-2 text-gray-600">Find answers to common questions.</p>
              </button>
            </motion.li>
          </motion.ul>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
